import React, { useState } from 'react';
import {
  Sun,
  Moon,
  Sparkles,
  Leaf,
  TrendingUp,
  Users,
  Search,
  Mail,
  Settings,
  User,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { resolveLanguageCode } from '../utils/locale';
import { useToast } from '../components/ui';
import { BRAND } from '../config/brand';
import {
  Card,
  Button,
  Input,
  Badge,
  Toggle,
  Modal,
  StatCard,
  Skeleton,
  Dropdown
} from '../components/ui';
import CodeBlock from '../components/CodeBlock';
import '../App.css';

const SHOWCASE_COPY = {
  en: {
    header: {
      subtitle: 'Premium Component Library',
      toggleThemeAria: 'Toggle theme',
    },
    hero: {
      badge: 'Premium Design System',
      titleLine1: 'Luxurious Components for',
      titleLine2: 'Modern Agriculture',
      description: 'A complete, accessible component library built with React. Features 10 core components with exact design specifications, dark mode support, and TypeScript-ready architecture.',
      exploreButton: 'Explore Components',
      githubButton: 'View on GitHub',
    },
    stats: {
      components: 'Components',
      designTokens: 'Design Tokens',
      accessibility: 'Accessibility',
    },
    intro: {
      title: 'Component Showcase',
      description: 'Interactive examples of all 10 core components. Click and interact to see them in action.',
    },
    sections: {
      card: {
        title: 'Card',
        description: 'Premium container component with hover effects and flexible padding options.',
        smallTitle: 'Small Padding',
        smallText: 'This card has small padding (16px)',
        mediumTitle: 'Medium + Hoverable',
        mediumText: 'Hover over me to see the effect!',
        largeTitle: 'Large Padding',
        largeText: 'This card has large padding (32px)',
      },
      button: {
        title: 'Button',
        description: 'Versatile button with 4 variants, 3 sizes, icon support, and loading states.',
        variants: 'Variants',
        sizes: 'Sizes',
        withIcons: 'With Icons & Loading',
        primary: 'Primary',
        secondary: 'Secondary',
        ghost: 'Ghost',
        danger: 'Danger',
        small: 'Small',
        medium: 'Medium',
        large: 'Large',
        withIcon: 'With Icon',
        loading: 'Loading...',
        clickToLoad: 'Click to Load',
      },
      input: {
        title: 'Input',
        description: 'Clean input field with label, helper text, error states, and icon support.',
        emailLabel: 'Email Address',
        emailPlaceholder: 'you@example.com',
        emailHelper: "We'll never share your email",
        searchLabel: 'Search',
        searchPlaceholder: 'Search...',
        errorLabel: 'Email (Error)',
        errorPlaceholder: 'invalid@email',
        errorMessage: 'Please enter a valid email address',
      },
      badge: {
        title: 'Badge',
        description: 'Pill-style badge with 5 semantic variants and optional dot indicator.',
        variants: 'Variants',
        withDot: 'With Dot Indicator',
        sizes: 'Sizes',
        success: 'Success',
        warning: 'Warning',
        danger: 'Danger',
        info: 'Info',
        neutral: 'Neutral',
        online: 'Online',
        away: 'Away',
        offline: 'Offline',
        smallBadge: 'Small Badge',
        mediumBadge: 'Medium Badge',
      },
      toggle: {
        title: 'Toggle Switch',
        description: 'Smooth animated toggle with keyboard accessibility and disabled states.',
        interactive: 'Interactive',
        sizes: 'Sizes',
        enableNotifications: 'Enable notifications',
        darkModeDisabled: 'Dark mode (disabled)',
        smallToggle: 'Small toggle',
        mediumToggle: 'Medium toggle',
      },
      modal: {
        title: 'Modal',
        description: 'Full-featured modal with backdrop, ESC key support, and focus management.',
        openButton: 'Open Modal',
        modalTitle: 'Premium Modal',
        cancel: 'Cancel',
        confirm: 'Confirm',
        body: 'This is a premium modal component with smooth animations, backdrop overlay, ESC key support, and focus management. It also supports different sizes (sm, md, lg, xl) and optional footer actions.',
      },
      toast: {
        title: 'Toast Notifications',
        description: 'Auto-dismissing toast notifications with 4 types and slide-in animation.',
        buttons: {
          success: 'Success Toast',
          info: 'Info Toast',
          warning: 'Warning Toast',
          error: 'Error Toast',
        },
        notifications: {
          success: {
            title: 'Success',
            message: 'Action completed successfully. This message will auto-dismiss in 4 seconds.',
          },
          info: {
            title: 'Info',
            message: 'Here is a quick update. This message will auto-dismiss in 4 seconds.',
          },
          warning: {
            title: 'Warning',
            message: 'Please review this warning. This message will auto-dismiss in 4 seconds.',
          },
          error: {
            title: 'Error',
            message: 'Something went wrong. This message will auto-dismiss in 4 seconds.',
          },
        },
      },
      statCard: {
        title: 'Stat Card',
        description: 'Display metrics with icons, values, labels, and trend indicators.',
        totalUsers: 'Total Users',
        activeFarms: 'Active Farms',
        monthlyRevenue: 'Monthly Revenue',
      },
      skeleton: {
        title: 'Skeleton Loader',
        description: 'Shimmer loading state with support for text, circles, and custom shapes.',
      },
      dropdown: {
        title: 'Dropdown Menu',
        description: 'Refined dropdown with keyboard navigation, icons, and dividers.',
        accountMenu: 'Account Menu',
        profile: 'Profile',
        settings: 'Settings',
        messages: 'Messages',
        logout: 'Logout',
      },
    },
    footer: {
      line1: 'Built with React, designed for premium agricultural platforms.',
      line2: '© 2024 AgriLink. All rights reserved.',
    },
    code: {
      cardTitle: 'Card Title',
      cardContent: 'Card content goes here',
      primaryButton: 'Primary Button',
      withIcon: 'With Icon',
      helperText: 'Helper text',
      errorMessage: 'Error message',
      withDot: 'With Dot',
      smallSize: 'Small Size',
      enableFeature: 'Enable feature',
      modalTitle: 'Modal Title',
      modalContent: 'Modal content',
      successTitle: 'Success!',
      completedMessage: 'Operation completed',
      menu: 'Menu',
      profile: 'Profile',
      logout: 'Logout',
      totalUsers: 'Total Users',
    },
  },
  it: {
    header: {
      subtitle: 'Libreria Componenti Premium',
      toggleThemeAria: 'Cambia tema',
    },
    hero: {
      badge: 'Design System Premium',
      titleLine1: "Componenti di alta qualità per l'",
      titleLine2: 'agricoltura moderna',
      description: 'Una libreria di componenti completa e accessibile costruita con React. Include 10 componenti fondamentali con specifiche di design precise, supporto dark mode e architettura pronta per TypeScript.',
      exploreButton: 'Esplora componenti',
      githubButton: 'Vedi su GitHub',
    },
    stats: {
      components: 'Componenti',
      designTokens: 'Token di design',
      accessibility: 'Accessibilità',
    },
    intro: {
      title: 'Vetrina componenti',
      description: 'Esempi interattivi di tutti i 10 componenti principali. Clicca e interagisci per vederli in azione.',
    },
    sections: {
      card: {
        title: 'Card',
        description: 'Contenitore premium con effetto hover e opzioni di padding flessibili.',
        smallTitle: 'Padding piccolo',
        smallText: 'Questa card usa padding piccolo (16px)',
        mediumTitle: 'Medio + Hover',
        mediumText: "Passa il mouse per vedere l'effetto!",
        largeTitle: 'Padding grande',
        largeText: 'Questa card usa padding grande (32px)',
      },
      button: {
        title: 'Pulsante',
        description: 'Pulsante versatile con 4 varianti, 3 dimensioni, supporto icone e stato di caricamento.',
        variants: 'Varianti',
        sizes: 'Dimensioni',
        withIcons: 'Con icone e caricamento',
        primary: 'Primario',
        secondary: 'Secondario',
        ghost: 'Ghost',
        danger: 'Pericolo',
        small: 'Piccolo',
        medium: 'Medio',
        large: 'Grande',
        withIcon: 'Con icona',
        loading: 'Caricamento...',
        clickToLoad: 'Clicca per caricare',
      },
      input: {
        title: 'Input',
        description: 'Campo input pulito con etichetta, testo di supporto, stato errore e icone.',
        emailLabel: 'Indirizzo email',
        emailPlaceholder: 'tu@esempio.com',
        emailHelper: 'Non condivideremo mai la tua email',
        searchLabel: 'Cerca',
        searchPlaceholder: 'Cerca...',
        errorLabel: 'Email (errore)',
        errorPlaceholder: 'email@nonvalida',
        errorMessage: 'Inserisci un indirizzo email valido',
      },
      badge: {
        title: 'Badge',
        description: 'Badge a pillola con 5 varianti semantiche e indicatore punto opzionale.',
        variants: 'Varianti',
        withDot: 'Con indicatore punto',
        sizes: 'Dimensioni',
        success: 'Successo',
        warning: 'Avviso',
        danger: 'Pericolo',
        info: 'Info',
        neutral: 'Neutro',
        online: 'Online',
        away: 'Assente',
        offline: 'Offline',
        smallBadge: 'Badge piccolo',
        mediumBadge: 'Badge medio',
      },
      toggle: {
        title: 'Interruttore',
        description: 'Toggle animato con accessibilità da tastiera e stato disabilitato.',
        interactive: 'Interattivo',
        sizes: 'Dimensioni',
        enableNotifications: 'Abilita notifiche',
        darkModeDisabled: 'Modalità scura (disabilitata)',
        smallToggle: 'Toggle piccolo',
        mediumToggle: 'Toggle medio',
      },
      modal: {
        title: 'Modale',
        description: 'Modale completo con backdrop, supporto tasto ESC e gestione del focus.',
        openButton: 'Apri modale',
        modalTitle: 'Modale premium',
        cancel: 'Annulla',
        confirm: 'Conferma',
        body: 'Questo è un componente modale premium con animazioni fluide, overlay di sfondo, supporto ESC e gestione del focus. Supporta anche dimensioni diverse (sm, md, lg, xl) e azioni footer opzionali.',
      },
      toast: {
        title: 'Notifiche toast',
        description: 'Toast con chiusura automatica, 4 tipi e animazione di ingresso.',
        buttons: {
          success: 'Toast successo',
          info: 'Toast info',
          warning: 'Toast avviso',
          error: 'Toast errore',
        },
        notifications: {
          success: {
            title: 'Successo',
            message: "Azione completata con successo. Questo messaggio si chiuderà in 4 secondi.",
          },
          info: {
            title: 'Info',
            message: 'Aggiornamento rapido disponibile. Questo messaggio si chiuderà in 4 secondi.',
          },
          warning: {
            title: 'Avviso',
            message: 'Controlla questo avviso. Questo messaggio si chiuderà in 4 secondi.',
          },
          error: {
            title: 'Errore',
            message: 'Qualcosa è andato storto. Questo messaggio si chiuderà in 4 secondi.',
          },
        },
      },
      statCard: {
        title: 'Scheda statistica',
        description: 'Mostra metriche con icone, valori, etichette e indicatori di trend.',
        totalUsers: 'Utenti totali',
        activeFarms: 'Aziende attive',
        monthlyRevenue: 'Ricavo mensile',
      },
      skeleton: {
        title: 'Skeleton loader',
        description: 'Stato di caricamento shimmer con supporto per testo, cerchi e forme personalizzate.',
      },
      dropdown: {
        title: 'Menu a discesa',
        description: 'Dropdown curato con navigazione da tastiera, icone e divisori.',
        accountMenu: 'Menu account',
        profile: 'Profilo',
        settings: 'Impostazioni',
        messages: 'Messaggi',
        logout: 'Esci',
      },
    },
    footer: {
      line1: 'Creato con React, progettato per piattaforme agricole premium.',
      line2: '© 2024 AgriLink. Tutti i diritti riservati.',
    },
    code: {
      cardTitle: 'Titolo card',
      cardContent: 'Contenuto card',
      primaryButton: 'Pulsante primario',
      withIcon: 'Con icona',
      helperText: 'Testo di supporto',
      errorMessage: 'Messaggio di errore',
      withDot: 'Con punto',
      smallSize: 'Taglia piccola',
      enableFeature: 'Abilita funzione',
      modalTitle: 'Titolo modale',
      modalContent: 'Contenuto modale',
      successTitle: 'Successo!',
      completedMessage: 'Operazione completata',
      menu: 'Menu',
      profile: 'Profilo',
      logout: 'Esci',
      totalUsers: 'Utenti totali',
    },
  },
  es: {
    header: {
      subtitle: 'Biblioteca de componentes premium',
      toggleThemeAria: 'Cambiar tema',
    },
    hero: {
      badge: 'Sistema de diseño premium',
      titleLine1: 'Componentes de alta calidad para',
      titleLine2: 'la agricultura moderna',
      description: 'Una biblioteca de componentes completa y accesible construida con React. Incluye 10 componentes principales con especificaciones de diseño precisas, soporte de modo oscuro y arquitectura lista para TypeScript.',
      exploreButton: 'Explorar componentes',
      githubButton: 'Ver en GitHub',
    },
    stats: {
      components: 'Componentes',
      designTokens: 'Tokens de diseño',
      accessibility: 'Accesibilidad',
    },
    intro: {
      title: 'Vitrina de componentes',
      description: 'Ejemplos interactivos de los 10 componentes principales. Haz clic e interactúa para verlos en acción.',
    },
    sections: {
      card: {
        title: 'Card',
        description: 'Contenedor premium con efectos hover y opciones de padding flexibles.',
        smallTitle: 'Padding pequeño',
        smallText: 'Esta card usa padding pequeño (16px)',
        mediumTitle: 'Medio + Hover',
        mediumText: 'Pasa el cursor para ver el efecto.',
        largeTitle: 'Padding grande',
        largeText: 'Esta card usa padding grande (32px)',
      },
      button: {
        title: 'Botón',
        description: 'Botón versátil con 4 variantes, 3 tamaños, soporte de iconos y estado de carga.',
        variants: 'Variantes',
        sizes: 'Tamaños',
        withIcons: 'Con iconos y carga',
        primary: 'Primario',
        secondary: 'Secundario',
        ghost: 'Ghost',
        danger: 'Peligro',
        small: 'Pequeño',
        medium: 'Mediano',
        large: 'Grande',
        withIcon: 'Con icono',
        loading: 'Cargando...',
        clickToLoad: 'Haz clic para cargar',
      },
      input: {
        title: 'Input',
        description: 'Campo de entrada limpio con etiqueta, texto de ayuda, estado de error y soporte de iconos.',
        emailLabel: 'Correo electrónico',
        emailPlaceholder: 'tu@ejemplo.com',
        emailHelper: 'Nunca compartiremos tu correo',
        searchLabel: 'Buscar',
        searchPlaceholder: 'Buscar...',
        errorLabel: 'Correo (error)',
        errorPlaceholder: 'correo@invalido',
        errorMessage: 'Introduce un correo electrónico válido',
      },
      badge: {
        title: 'Badge',
        description: 'Badge estilo píldora con 5 variantes semánticas e indicador de punto opcional.',
        variants: 'Variantes',
        withDot: 'Con indicador de punto',
        sizes: 'Tamaños',
        success: 'Éxito',
        warning: 'Advertencia',
        danger: 'Peligro',
        info: 'Info',
        neutral: 'Neutro',
        online: 'En línea',
        away: 'Ausente',
        offline: 'Desconectado',
        smallBadge: 'Badge pequeño',
        mediumBadge: 'Badge mediano',
      },
      toggle: {
        title: 'Interruptor',
        description: 'Toggle animado con accesibilidad por teclado y estado deshabilitado.',
        interactive: 'Interactivo',
        sizes: 'Tamaños',
        enableNotifications: 'Activar notificaciones',
        darkModeDisabled: 'Modo oscuro (deshabilitado)',
        smallToggle: 'Toggle pequeño',
        mediumToggle: 'Toggle mediano',
      },
      modal: {
        title: 'Modal',
        description: 'Modal completo con fondo, soporte de tecla ESC y gestión del foco.',
        openButton: 'Abrir modal',
        modalTitle: 'Modal premium',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        body: 'Este es un componente modal premium con animaciones fluidas, superposición de fondo, soporte de tecla ESC y gestión de foco. También admite diferentes tamaños (sm, md, lg, xl) y acciones opcionales en el pie.',
      },
      toast: {
        title: 'Notificaciones toast',
        description: 'Toast con cierre automático, 4 tipos y animación de entrada.',
        buttons: {
          success: 'Toast de éxito',
          info: 'Toast de info',
          warning: 'Toast de advertencia',
          error: 'Toast de error',
        },
        notifications: {
          success: {
            title: 'Éxito',
            message: 'Acción completada correctamente. Este mensaje se cerrará en 4 segundos.',
          },
          info: {
            title: 'Info',
            message: 'Aquí tienes una actualización rápida. Este mensaje se cerrará en 4 segundos.',
          },
          warning: {
            title: 'Advertencia',
            message: 'Revisa esta advertencia. Este mensaje se cerrará en 4 segundos.',
          },
          error: {
            title: 'Error',
            message: 'Algo salió mal. Este mensaje se cerrará en 4 segundos.',
          },
        },
      },
      statCard: {
        title: 'Tarjeta estadística',
        description: 'Muestra métricas con iconos, valores, etiquetas e indicadores de tendencia.',
        totalUsers: 'Usuarios totales',
        activeFarms: 'Fincas activas',
        monthlyRevenue: 'Ingresos mensuales',
      },
      skeleton: {
        title: 'Skeleton loader',
        description: 'Estado de carga con shimmer compatible con texto, círculos y formas personalizadas.',
      },
      dropdown: {
        title: 'Menú desplegable',
        description: 'Dropdown refinado con navegación por teclado, iconos y divisores.',
        accountMenu: 'Menú de cuenta',
        profile: 'Perfil',
        settings: 'Configuración',
        messages: 'Mensajes',
        logout: 'Cerrar sesión',
      },
    },
    footer: {
      line1: 'Construido con React, diseñado para plataformas agrícolas premium.',
      line2: '© 2024 AgriLink. Todos los derechos reservados.',
    },
    code: {
      cardTitle: 'Título de la card',
      cardContent: 'Contenido de la card',
      primaryButton: 'Botón primario',
      withIcon: 'Con icono',
      helperText: 'Texto de ayuda',
      errorMessage: 'Mensaje de error',
      withDot: 'Con punto',
      smallSize: 'Tamaño pequeño',
      enableFeature: 'Activar función',
      modalTitle: 'Título del modal',
      modalContent: 'Contenido del modal',
      successTitle: '¡Éxito!',
      completedMessage: 'Operación completada',
      menu: 'Menú',
      profile: 'Perfil',
      logout: 'Cerrar sesión',
      totalUsers: 'Usuarios totales',
    },
  },
};

const buildCodeSnippets = (copy) => ({
  card: `<Card padding="md" bordered hoverable>\n  <h3>${copy.code.cardTitle}</h3>\n  <p>${copy.code.cardContent}</p>\n</Card>`,
  button: `<Button variant="primary" size="md">\n  ${copy.code.primaryButton}\n</Button>\n\n<Button icon={<Leaf />} loading={false}>\n  ${copy.code.withIcon}\n</Button>`,
  input: `<Input\n  label="${copy.sections.input.emailLabel}"\n  placeholder="${copy.sections.input.emailPlaceholder}"\n  value={value}\n  onChange={setValue}\n  helper="${copy.code.helperText}"\n  error="${copy.code.errorMessage}"\n  icon={<Search />}\n/>`,
  badge: `<Badge variant="success">${copy.sections.badge.success}</Badge>\n<Badge variant="warning" dot>${copy.code.withDot}</Badge>\n<Badge size="sm">${copy.code.smallSize}</Badge>`,
  toggle: `<Toggle\n  checked={checked}\n  onChange={setChecked}\n  label="${copy.code.enableFeature}"\n  size="md"\n/>`,
  modal: `<Modal\n  isOpen={isOpen}\n  onClose={() => setIsOpen(false)}\n  title="${copy.code.modalTitle}"\n  size="md"\n  footer={\n    <Button onClick={handleConfirm}>\n      ${copy.sections.modal.confirm}\n    </Button>\n  }\n>\n  <p>${copy.code.modalContent}</p>\n</Modal>`,
  toast: `const { addToast } = useToast();\n\naddToast({\n  type: 'success',\n  title: '${copy.code.successTitle}',\n  message: '${copy.code.completedMessage}',\n  duration: 4000\n});`,
  statCard: `<StatCard\n  label="${copy.code.totalUsers}"\n  value="2,543"\n  icon={<Users />}\n  trend={{ value: 12.5, direction: 'up' }}\n  color="success"\n/>`,
  skeleton: `<Skeleton width="200px" height="20px" />\n<Skeleton circle width="48px" />\n<Skeleton count={3} />`,
  dropdown: `<Dropdown\n  trigger={<Button>${copy.code.menu}</Button>}\n  items={[\n    { label: '${copy.code.profile}', icon: <User />, onClick: handleProfile },\n    { divider: true },\n    { label: '${copy.code.logout}', icon: <LogOut />, onClick: handleLogout }\n  ]}\n  align="left"\n/>`,
});

export function ComponentsShowcasePage() {
  const { i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { addToast } = useToast();

  const languageCode = resolveLanguageCode(i18n.language);
  const copy = SHOWCASE_COPY[languageCode] || SHOWCASE_COPY.en;
  const code = buildCodeSnippets(copy);

  // Component states for interactive demos
  const [inputValue, setInputValue] = useState('');
  const [toggleValue, setToggleValue] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const showToast = (type) => {
    const notification = copy.sections.toast.notifications[type] || copy.sections.toast.notifications.info;
    addToast({
      type,
      title: notification.title,
      message: notification.message,
      duration: 4000
    });
  };

  const handleLoadingDemo = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header__content">
          <div className="header__brand">
            <img src={BRAND.iconPath} alt={`${BRAND.name} logo`} className="header__logo" />
            <div>
              <h1 className="header__title">{BRAND.wordmark}</h1>
              <p className="header__subtitle">{copy.header.subtitle}</p>
            </div>
          </div>

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={copy.header.toggleThemeAria}
            data-testid="theme-toggle"
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero__content">
          <div className="hero__badge">
            <Sparkles size={16} />
            <span>{copy.hero.badge}</span>
          </div>
          <h2 className="hero__title">{copy.hero.titleLine1}<br />{copy.hero.titleLine2}</h2>
          <p className="hero__description">
            {copy.hero.description}
          </p>
          <div className="hero__actions">
            <Button
              variant="primary"
              size="lg"
              onClick={() => document.getElementById('components').scrollIntoView({ behavior: 'smooth' })}
            >
              {copy.hero.exploreButton}
            </Button>
            <Button variant="secondary" size="lg">
              {copy.hero.githubButton}
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="stats__grid">
          <StatCard
            label={copy.stats.components}
            value="10"
            icon={<Sparkles size={24} />}
            color="primary"
          />
          <StatCard
            label={copy.stats.designTokens}
            value="50+"
            icon={<TrendingUp size={24} />}
            trend={{ value: 25, direction: 'up' }}
            color="success"
          />
          <StatCard
            label={copy.stats.accessibility}
            value="AA"
            icon={<Users size={24} />}
            color="primary"
          />
        </div>
      </section>

      {/* Components Section */}
      <section className="components" id="components">
        <div className="components__content">
          <h2 className="section-title">{copy.intro.title}</h2>
          <p className="section-description">
            {copy.intro.description}
          </p>

          {/* 1. Card Component */}
          <ComponentSection
            title={copy.sections.card.title}
            description={copy.sections.card.description}
          >
            <div className="demo-grid">
              <Card padding="sm" bordered>
                <h3 className="demo-card-title">{copy.sections.card.smallTitle}</h3>
                <p className="demo-card-text">{copy.sections.card.smallText}</p>
              </Card>
              <Card padding="md" bordered hoverable>
                <h3 className="demo-card-title">{copy.sections.card.mediumTitle}</h3>
                <p className="demo-card-text">{copy.sections.card.mediumText}</p>
              </Card>
              <Card padding="lg" bordered>
                <h3 className="demo-card-title">{copy.sections.card.largeTitle}</h3>
                <p className="demo-card-text">{copy.sections.card.largeText}</p>
              </Card>
            </div>
            <CodeBlock code={code.card} />
          </ComponentSection>

          {/* 2. Button Component */}
          <ComponentSection
            title={copy.sections.button.title}
            description={copy.sections.button.description}
          >
            <div className="demo-section">
              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.button.variants}</h4>
                <div className="demo-row">
                  <Button variant="primary">{copy.sections.button.primary}</Button>
                  <Button variant="secondary">{copy.sections.button.secondary}</Button>
                  <Button variant="ghost">{copy.sections.button.ghost}</Button>
                  <Button variant="danger">{copy.sections.button.danger}</Button>
                </div>
              </div>

              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.button.sizes}</h4>
                <div className="demo-row">
                  <Button size="sm">{copy.sections.button.small}</Button>
                  <Button size="md">{copy.sections.button.medium}</Button>
                  <Button size="lg">{copy.sections.button.large}</Button>
                </div>
              </div>

              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.button.withIcons}</h4>
                <div className="demo-row">
                  <Button icon={<Leaf size={18} />} iconPosition="left">
                    {copy.sections.button.withIcon}
                  </Button>
                  <Button
                    loading={loading}
                    onClick={handleLoadingDemo}
                  >
                    {loading ? copy.sections.button.loading : copy.sections.button.clickToLoad}
                  </Button>
                </div>
              </div>
            </div>
            <CodeBlock code={code.button} />
          </ComponentSection>

          {/* 3. Input Component */}
          <ComponentSection
            title={copy.sections.input.title}
            description={copy.sections.input.description}
          >
            <div className="demo-section">
              <div className="demo-grid demo-grid--inputs">
                <Input
                  label={copy.sections.input.emailLabel}
                  placeholder={copy.sections.input.emailPlaceholder}
                  value={inputValue}
                  onChange={setInputValue}
                  helper={copy.sections.input.emailHelper}
                />
                <Input
                  label={copy.sections.input.searchLabel}
                  placeholder={copy.sections.input.searchPlaceholder}
                  icon={<Search size={20} />}
                  value={inputValue}
                  onChange={setInputValue}
                />
                <Input
                  label={copy.sections.input.errorLabel}
                  placeholder={copy.sections.input.errorPlaceholder}
                  value="invalid"
                  onChange={() => {}}
                  error={copy.sections.input.errorMessage}
                />
              </div>
            </div>
            <CodeBlock code={code.input} />
          </ComponentSection>

          {/* 4. Badge Component */}
          <ComponentSection
            title={copy.sections.badge.title}
            description={copy.sections.badge.description}
          >
            <div className="demo-section">
              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.badge.variants}</h4>
                <div className="demo-row">
                  <Badge variant="success">{copy.sections.badge.success}</Badge>
                  <Badge variant="warning">{copy.sections.badge.warning}</Badge>
                  <Badge variant="danger">{copy.sections.badge.danger}</Badge>
                  <Badge variant="info">{copy.sections.badge.info}</Badge>
                  <Badge variant="neutral">{copy.sections.badge.neutral}</Badge>
                </div>
              </div>

              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.badge.withDot}</h4>
                <div className="demo-row">
                  <Badge variant="success" dot>{copy.sections.badge.online}</Badge>
                  <Badge variant="warning" dot>{copy.sections.badge.away}</Badge>
                  <Badge variant="danger" dot>{copy.sections.badge.offline}</Badge>
                </div>
              </div>

              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.badge.sizes}</h4>
                <div className="demo-row">
                  <Badge size="sm">{copy.sections.badge.smallBadge}</Badge>
                  <Badge size="md">{copy.sections.badge.mediumBadge}</Badge>
                </div>
              </div>
            </div>
            <CodeBlock code={code.badge} />
          </ComponentSection>

          {/* 5. Toggle Component */}
          <ComponentSection
            title={copy.sections.toggle.title}
            description={copy.sections.toggle.description}
          >
            <div className="demo-section">
              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.toggle.interactive}</h4>
                <div className="demo-column">
                  <Toggle
                    checked={toggleValue}
                    onChange={setToggleValue}
                    label={copy.sections.toggle.enableNotifications}
                  />
                  <Toggle
                    checked={true}
                    onChange={() => {}}
                    label={copy.sections.toggle.darkModeDisabled}
                    disabled
                  />
                </div>
              </div>

              <div className="demo-subsection">
                <h4 className="demo-subtitle">{copy.sections.toggle.sizes}</h4>
                <div className="demo-column">
                  <Toggle
                    checked={toggleValue}
                    onChange={setToggleValue}
                    label={copy.sections.toggle.smallToggle}
                    size="sm"
                  />
                  <Toggle
                    checked={toggleValue}
                    onChange={setToggleValue}
                    label={copy.sections.toggle.mediumToggle}
                    size="md"
                  />
                </div>
              </div>
            </div>
            <CodeBlock code={code.toggle} />
          </ComponentSection>

          {/* 6. Modal Component */}
          <ComponentSection
            title={copy.sections.modal.title}
            description={copy.sections.modal.description}
          >
            <div className="demo-section">
              <Button onClick={() => setModalOpen(true)}>
                {copy.sections.modal.openButton}
              </Button>

              <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={copy.sections.modal.modalTitle}
                size="md"
                footer={
                  <>
                    <Button variant="ghost" onClick={() => setModalOpen(false)}>
                      {copy.sections.modal.cancel}
                    </Button>
                    <Button variant="primary" onClick={() => setModalOpen(false)}>
                      {copy.sections.modal.confirm}
                    </Button>
                  </>
                }
              >
                <p style={{ color: 'var(--color-gray-700)', lineHeight: '1.6' }}>
                  {copy.sections.modal.body}
                </p>
              </Modal>
            </div>
            <CodeBlock code={code.modal} />
          </ComponentSection>

          {/* 7. Toast Component */}
          <ComponentSection
            title={copy.sections.toast.title}
            description={copy.sections.toast.description}
          >
            <div className="demo-section">
              <div className="demo-row">
                <Button
                  variant="primary"
                  onClick={() => showToast('success')}
                >
                  {copy.sections.toast.buttons.success}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => showToast('info')}
                >
                  {copy.sections.toast.buttons.info}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => showToast('warning')}
                >
                  {copy.sections.toast.buttons.warning}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => showToast('error')}
                >
                  {copy.sections.toast.buttons.error}
                </Button>
              </div>
            </div>
            <CodeBlock code={code.toast} />
          </ComponentSection>

          {/* 8. Stat Card Component */}
          <ComponentSection
            title={copy.sections.statCard.title}
            description={copy.sections.statCard.description}
          >
            <div className="demo-grid">
              <StatCard
                label={copy.sections.statCard.totalUsers}
                value="2,543"
                icon={<Users size={24} />}
                trend={{ value: 12.5, direction: 'up' }}
                color="success"
              />
              <StatCard
                label={copy.sections.statCard.activeFarms}
                value="1,234"
                icon={<Leaf size={24} />}
                trend={{ value: 8.3, direction: 'up' }}
                color="primary"
              />
              <StatCard
                label={copy.sections.statCard.monthlyRevenue}
                value="$45.2K"
                icon={<TrendingUp size={24} />}
                trend={{ value: 3.2, direction: 'down' }}
                color="warning"
              />
            </div>
            <CodeBlock code={code.statCard} />
          </ComponentSection>

          {/* 9. Skeleton Component */}
          <ComponentSection
            title={copy.sections.skeleton.title}
            description={copy.sections.skeleton.description}
          >
            <div className="demo-section">
              <Card padding="md" bordered>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                  <Skeleton circle width="48px" height="48px" />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="200px" height="20px" />
                    <div style={{ marginTop: '8px' }}>
                      <Skeleton width="150px" height="16px" />
                    </div>
                  </div>
                </div>
                <Skeleton width="100%" height="100px" />
              </Card>
            </div>
            <CodeBlock code={code.skeleton} />
          </ComponentSection>

          {/* 10. Dropdown Component */}
          <ComponentSection
            title={copy.sections.dropdown.title}
            description={copy.sections.dropdown.description}
          >
            <div className="demo-section">
              <Dropdown
                trigger={
                  <Button variant="secondary" icon={<ChevronDown size={18} />} iconPosition="right">
                    {copy.sections.dropdown.accountMenu}
                  </Button>
                }
                items={[
                  { label: copy.sections.dropdown.profile, icon: <User size={18} />, onClick: () => showToast('info') },
                  { label: copy.sections.dropdown.settings, icon: <Settings size={18} />, onClick: () => showToast('info') },
                  { label: copy.sections.dropdown.messages, icon: <Mail size={18} />, onClick: () => showToast('info') },
                  { divider: true },
                  { label: copy.sections.dropdown.logout, icon: <LogOut size={18} />, onClick: () => showToast('info') },
                ]}
                align="left"
              />
            </div>
            <CodeBlock code={code.dropdown} />
          </ComponentSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer__content">
          <p className="footer__text">
            {copy.footer.line1}
          </p>
          <p className="footer__text">
            {copy.footer.line2}
          </p>
        </div>
      </footer>
    </div>
  );
}

// Component Section Wrapper
function ComponentSection({ title, description, children }) {
  return (
    <div className="component-section">
      <div className="component-section__header">
        <h3 className="component-section__title">{title}</h3>
        <p className="component-section__description">{description}</p>
      </div>
      <div className="component-section__body">
        {children}
      </div>
    </div>
  );
}
